// CategoryId.jsx
import React from 'react';

import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { getSupportedLanguages } from '../selector/getRemoteConfigComputedData.js';
import fetchFeeds, { getReturnType } from '../action/fetchFeeds.js';
import { CATEGORY_FEED_ALL } from '../RemoteConfigKeys.js';
import { prepareCollectionPageMeta } from '../resource/prepareCollectionPageMeta.js';
import UniversalComponent from '../component/UniversalComponent.jsx';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { fetchFeedTotalCount } from '../resource/fetchFeedTotalCount.js';
import { generateCollectionPageJsonLd } from '../resource/generateCollectionPageJsonLd.js';
import shouldSkipSSRFetch from '../resource/shouldSkipSSRFetch.js';
import { ITEM_COUNT_EAGER_RENDER } from '../resource/feedConstants.js';
import { sanitizeFeedQuery } from '../resource/feedUtils.js';
import {
  CLEAR_NETWORKING_NODES,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const loader = async ({
  context,
  params,
  request,
  i18nInstance,
  language,
}) => {
  const log = legacySSRLog.extend('CategoryId loader()');
  const errorLog = legacySSRError.extend('CategoryId loader()');
  log('start', { context, params, request });

  const isServer = typeof window === 'undefined';
  // The redux store is provided via loader context (server: requestContext, client: getContext).
  // Loaders dispatch directly into it — the same store that
  // is rendered / serialized to __PRELOADED_STATE__
  // the data is present before the first render (SSR and navigation).
  const store = context?.store;
  if (!store) {
    if (isServer) {
      errorLog('context.store is missing on server', { params });
      throw new Error(
        'CategoryIdRoute loader requires context.store (server: query requestContext)'
      );
    }
    log('context.store missing on client, skipping loader');
    return { params, shouldSkipHydration: true };
  }

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    let state = store.getState();

    const skipSSRFetch =
      isServer &&
      shouldSkipSSRFetch({
        requestUrl: new URL(request.url),
        state,
      });

    if (skipSSRFetch) {
      log('skip SSR fetch due to buster query');
      // shouldSkipHydration: incomplete result — keep it out of
      // __staticRouterHydrationData so the client loader re-runs on the
      // initial load instead of hydrating a data-less page.
      return { params, shouldSkipHydration: true };
    }

    // Get category feeds from remote config
    const { categoryId } = params;
    const categoryFeeds = getRemoteConfigData(
      state,
      CATEGORY_FEED_ALL,
      undefined,
      {
        CATEGORY: categoryId,
      }
    );

    // Fetch all category feeds count and prefetch first page
    let totalFeedCount = 0;
    if (categoryFeeds && categoryFeeds.length > 0) {
      const [feedsResult, feedCountResults] = await Promise.all([
        isServer
          ? Promise.allSettled(
              categoryFeeds.map(categoryFeedName =>
                store.dispatch(
                  fetchFeeds({
                    page: 1,
                    limit: ITEM_COUNT_EAGER_RENDER,
                    type: categoryFeedName,
                    httpProxyHeaders,
                  })
                )
              )
            )
          : Promise.resolve(null),
        Promise.allSettled(
          categoryFeeds.map(categoryFeedName =>
            fetchFeedTotalCount({
              feedNameWithQuery: categoryFeedName,
              httpProxyHeaders,
            })
          )
        ),
      ]);
      // Sum up all feed counts
      totalFeedCount = feedCountResults.reduce((sum, result) => {
        return sum + (result.status === 'fulfilled' ? result.value : 0);
      }, 0);

      if (Array.isArray(feedsResult)) {
        feedsResult.forEach((result, i) => {
          if (
            result.status === 'fulfilled' &&
            result.value?.type === SET_NETWORKING_ERROR
          ) {
            const categoryFeedName = categoryFeeds[i];
            const sanitizedType = sanitizeFeedQuery({
              feedName: categoryFeedName,
            });
            const returnType = getReturnType({ feedName: sanitizedType });
            const networkingClearPath = [
              'home',
              returnType,
              sanitizedType,
              1,
            ].map(p => `${p}`);
            store.dispatch({
              type: CLEAR_NETWORKING_NODES,
              payload: { selectPaths: [networkingClearPath] },
            });
          }
        });
      }
    }

    state = store.getState();
    const storeData = state.toJS();
    log('fetched data', { store: storeData, totalFeedCount });

    // Use i18n instance from loader
    const { t } = i18nInstance;

    // Get category name from translation
    const categoryName = t(`category_${categoryId}`, {
      ns: TranslationNamespace.FEED,
    });

    // Prepare title and description
    const title = t('seo_category_all_title', {
      ns: TranslationNamespace.SEO,
      category: categoryName,
    });

    const description =
      t(`seo_meta_template_description_category_${categoryId}`, {
        ns: TranslationNamespace.SEO,
      }) ||
      t('seo_category_all_description', {
        ns: TranslationNamespace.SEO,
        category: categoryName,
      });

    const availableLanguages = getSupportedLanguages(state);
    const canonicalPath = `/category/${categoryId}`;
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
      language,
    });

    // Generate JSON-LD
    const hostname = request?.headers?.get('host') || 'swag.live';
    const origin = `https://${hostname}`;

    const jsonLd = await generateCollectionPageJsonLd({
      origin,
      categoryId,
      state,
      i18n: i18nInstance,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareCollectionPageMeta({
      state,
      title,
      description,
      keywords: [categoryName, 'SWAG', 'category'],
      hostname,
      canonical,
      alternates,
      jsonLd,
    });

    log('meta data prepared', { meta });

    return {
      params,
      meta,
      staticContext: {
        requestHeaders: {
          hostname,
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });
    // shouldSkipHydration: a failed server fetch must not be hydrated, so the
    // client loader re-runs on the initial load and retries the fetch.
    return { params, shouldSkipHydration: true };
  }
};

const CategoryIdRoute = () => {
  return (
    <UniversalComponent
      filename="Container_MessageCategoryTabAllInOne"
      importer={
        /* #__LOADABLE__ */ () =>
          import('../container/MessageCategoryTabAllInOne.js')
      }
    />
  );
};

export default CategoryIdRoute;
